// Animated tokens (issue 1176, TOKEN-017): a token's art that moves — an animated GIF, WebP or PNG (APNG), or a short
// WebM loop — plays on its sprite while the board stays within its smoothness budget (design/25).
//
// The bytes are fetched with the bearer into a blob like every picture (`pictureBlob`), never a public URL. A WebM is a
// muted, looping <video> on that blob's object URL (CSP `media-src 'self' blob:`), drawn as Pixi's video texture. An
// animated picture is decoded frame by frame by the browser's own ImageDecoder (WebCodecs) onto one canvas, drawn as a
// canvas texture; a browser without ImageDecoder shows the first frame still, as an <img> would. One player per
// picture, however many tokens wear it.
//
// The board's frame loop renders on demand (PR #991): an animation does NOT make it draw every frame. `AnimationLoop`
// steps the animations on screen at most `ANIMATION_FPS` times a second, each step that changed a frame marks the board
// dirty once, and with none on screen — off-screen, absent from this viewer's state, or the setting off — it does no
// work and the loop idles again. This module holds no Pixi: the board hands it the two texture makers.

export const ANIMATION_FPS = 24;
export const ANIMATION_STEP_MS = 1000 / ANIMATION_FPS;

// ── The setting ───────────────────────────────────────────────────────────

const KEY = 'vtt.animate';
const reducedMotion = (): boolean => { try { return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };
const listeners = new Set<(on: boolean) => void>();
/** "Animate tokens": each person's own, kept in this browser; on by default, off by default where the system asks for less motion. Off shows the first frame still. */
export const animateSetting = {
  get(): boolean {
    try { const v = localStorage.getItem(KEY); if (v === '1') return true; if (v === '0') return false; } catch { /* storage refused: the default */ }
    return !reducedMotion();
  },
  set(on: boolean): void {
    try { localStorage.setItem(KEY, on ? '1' : '0'); } catch { /* storage refused: this page only */ }
    for (const l of listeners) l(on);
  },
  subscribe(l: (on: boolean) => void): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
};

// ── What moves ────────────────────────────────────────────────────────────

/**
 * Whether a picture's bytes say it moves, read from the header as the server's sniffer reads it: the VP8X animation bit
 * of a WebP, an acTL chunk before a PNG's first IDAT, a second image in a GIF. A WebM always does.
 */
export function movesFromHeader(type: string, b: Uint8Array): boolean {
  const ascii = (s: number, e: number) => String.fromCharCode(...b.subarray(s, e));
  if (type.startsWith('video/')) return true;
  if (b.length >= 21 && ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return ascii(12, 16) === 'VP8X' && (b[20]! & 0x02) !== 0;
  if (b.length >= 8 && b[0] === 0x89 && ascii(1, 4) === 'PNG') {
    let at = 8;
    while (at + 8 <= b.length) {
      const len = ((b[at]! << 24) >>> 0) + (b[at + 1]! << 16) + (b[at + 2]! << 8) + b[at + 3]!;
      const t = ascii(at + 4, at + 8);
      if (t === 'acTL') return true;
      if (t === 'IDAT' || t === 'IEND') return false;
      at += 12 + len;
    }
    return false;
  }
  if (b.length >= 6 && ascii(0, 3) === 'GIF') {
    // Count image descriptors (0x2C) by walking the blocks; two is enough.
    const packed = b[10] ?? 0;
    let at = 13 + ((packed & 0x80) ? 3 * (1 << ((packed & 7) + 1)) : 0);
    const sub = (p: number) => { while (p < b.length) { const n = b[p]!; p += 1; if (n === 0) return p; p += n; } return -1; };
    let frames = 0;
    while (at > 0 && at < b.length) {
      const tag = b[at]!;
      if (tag === 0x3b) break;
      if (tag === 0x21) { at = sub(at + 2); continue; }
      if (tag !== 0x2c) break;
      frames += 1;
      if (frames > 1) return true;
      const local = b[at + 9] ?? 0;
      at = sub(at + 10 + ((local & 0x80) ? 3 * (1 << ((local & 7) + 1)) : 0) + 1);
    }
    return false;
  }
  return false;
}

/** A texture the board can draw and be told to re-upload. */
export interface MovingTexture<T> { texture: T; update(): void; destroy(): void }

/** What making a player needs from the page: the board passes Pixi's makers; a test passes fakes. */
export interface MovingEnv<T> {
  videoTexture(video: HTMLVideoElement): MovingTexture<T>;
  canvasTexture(canvas: HTMLCanvasElement): MovingTexture<T>;
  createVideo?: () => HTMLVideoElement;
  createCanvas?: () => HTMLCanvasElement;
  objectUrl?: (b: Blob) => string;
  revokeUrl?: (u: string) => void;
  imageDecoder?: ImageDecoderCtor | null;
}

/** The slice of WebCodecs' ImageDecoder this uses (not in every TypeScript lib yet). */
export interface DecodedFrame { image: { displayWidth: number; displayHeight: number; duration: number | null; close(): void } & CanvasImageSource }
export interface ImageDecoderLike {
  tracks: { ready: Promise<void>; selectedTrack: { animated: boolean; frameCount: number } | null };
  completed: Promise<void>;
  decode(o: { frameIndex: number }): Promise<DecodedFrame>;
  close(): void;
}
export type ImageDecoderCtor = new (init: { data: ArrayBuffer | ReadableStream; type: string }) => ImageDecoderLike;

/** One picture playing: its texture, shared by every token wearing it. */
export interface Moving<T> {
  readonly kind: 'video' | 'frames';
  readonly texture: T;
  /** Advance to the frame due at `now`, if one is; a frame drawn calls the onChange the player was made with. */
  step(now: number): void;
  /** Stop spending: a video pauses, frames stop decoding. The texture keeps the frame it shows. */
  pause(): void;
  destroy(): void;
}

/** A blob's bytes (FileReader where a Blob has no arrayBuffer, as in jsdom). */
function bytesOf(b: Blob): Promise<ArrayBuffer> {
  if (typeof b.arrayBuffer === 'function') return b.arrayBuffer();
  return new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result as ArrayBuffer); r.onerror = () => reject(r.error); r.readAsArrayBuffer(b); });
}

const defaultDecoder = (): ImageDecoderCtor | null => (typeof window !== 'undefined' && 'ImageDecoder' in window ? (window as unknown as { ImageDecoder: ImageDecoderCtor }).ImageDecoder : null);

/**
 * The player for a moving picture's blob, or null when it does not move here (a still picture, or an animated one in a
 * browser without ImageDecoder: the caller draws it still, as before). It starts paused on its first frame.
 */
export async function makeMoving<T>(blob: Blob, env: MovingEnv<T>, onChange: () => void): Promise<Moving<T> | null> {
  const head = new Uint8Array(await bytesOf(blob.slice(0, 64 * 1024)));
  if (!movesFromHeader(blob.type, head)) return null;
  if (blob.type.startsWith('video/')) {
    const video = env.createVideo ? env.createVideo() : document.createElement('video');
    const url = (env.objectUrl ?? URL.createObjectURL)(blob);
    video.muted = true; video.loop = true; video.playsInline = true; video.preload = 'auto';
    video.setAttribute('muted', ''); video.setAttribute('playsinline', '');
    video.src = url;
    await new Promise<void>((resolve) => {
      if (video.readyState >= 2) { resolve(); return; }
      const done = () => { video.removeEventListener('loadeddata', done); video.removeEventListener('error', done); resolve(); };
      video.addEventListener('loadeddata', done); video.addEventListener('error', done);
    });
    const tex = env.videoTexture(video);
    let shown = -1;
    let wantPlay = false;
    return {
      kind: 'video',
      texture: tex.texture,
      step() {
        if (!wantPlay) { wantPlay = true; void video.play()?.catch(() => { wantPlay = false; }); }
        if (video.currentTime !== shown) { shown = video.currentTime; tex.update(); onChange(); }
      },
      pause() { if (wantPlay) { wantPlay = false; video.pause(); } },
      destroy() { wantPlay = false; video.pause(); video.removeAttribute('src'); video.load?.(); (env.revokeUrl ?? URL.revokeObjectURL)(url); tex.destroy(); },
    };
  }
  const Decoder = env.imageDecoder === undefined ? defaultDecoder() : env.imageDecoder;
  if (!Decoder) return null;
  let decoder: ImageDecoderLike;
  try {
    decoder = new Decoder({ data: await bytesOf(blob), type: blob.type });
    await decoder.tracks.ready;
    await decoder.completed;
  } catch { return null; }
  const count = decoder.tracks.selectedTrack?.frameCount ?? 0;
  if (count < 2) { decoder.close(); return null; }
  const canvas = env.createCanvas ? env.createCanvas() : document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const draw = async (i: number): Promise<number> => {
    const { image } = await decoder.decode({ frameIndex: i });
    try {
      if (canvas.width !== image.displayWidth || canvas.height !== image.displayHeight) { canvas.width = image.displayWidth; canvas.height = image.displayHeight; }
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      ctx?.drawImage(image, 0, 0);
      // A frame's duration is in microseconds; a GIF's 0 ("as fast as you can") plays as browsers do, at 100 ms.
      const d = image.duration ? image.duration / 1000 : 0;
      return d >= 20 ? d : 100;
    } finally { image.close(); }
  };
  let frame = 0;
  let dueAt = 0;
  let busy = false;
  let dead = false;
  let lastDuration = await draw(0).catch(() => 100);
  const tex = env.canvasTexture(canvas);
  return {
    kind: 'frames',
    texture: tex.texture,
    step(now) {
      if (busy || dead) return;
      if (dueAt === 0) { dueAt = now + lastDuration; return; }
      if (now < dueAt) return;
      busy = true;
      const next = (frame + 1) % count;
      void draw(next).then((d) => {
        if (dead) return;
        frame = next; lastDuration = d;
        // Fall behind a whole loop and the clock is reset rather than racing to catch up (design/25 §4: shorter, never later).
        dueAt = now - dueAt > d * count ? now + d : dueAt + d;
        tex.update(); onChange();
      }).catch(() => undefined).finally(() => { busy = false; });
    },
    pause() { dueAt = 0; },
    destroy() { dead = true; decoder.close(); tex.destroy(); },
  };
}

// ── The loop ──────────────────────────────────────────────────────────────

/**
 * Steps the animations on screen, at most ANIMATION_FPS times a second. `frame` is called on every tick of the board's
 * loop with the players visible now; the ones that were playing and are not any more are paused. Returns whether it
 * stepped (the readout counts it). With nothing visible, or the setting off, it steps nothing.
 */
export class AnimationLoop<T> {
  private playing = new Set<Moving<T>>();
  private lastStep = -Infinity;
  steps = 0;
  frame(now: number, visible: Iterable<Moving<T>>, on: boolean): boolean {
    const now_ = new Set(on ? visible : []);
    for (const m of this.playing) if (!now_.has(m)) m.pause();
    this.playing = now_;
    animationStats.playing = now_.size;
    if (!now_.size) return false;
    if (now - this.lastStep < ANIMATION_STEP_MS - 0.5) return false;
    // The cap kept on average, not per tick: a 60 Hz loop steps on the 2nd or 3rd tick in turn (24 a second, not 20);
    // a loop that fell far behind starts counting again from now.
    this.lastStep = now - this.lastStep > 2 * ANIMATION_STEP_MS ? now : this.lastStep + ANIMATION_STEP_MS;
    for (const m of now_) m.step(now);
    this.steps += 1;
    animationStats.steps += 1;
    return true;
  }
  /** Everything stops: the board is going away or the setting went off. */
  stop(): void { for (const m of this.playing) m.pause(); this.playing.clear(); animationStats.playing = 0; }
}

/** What the `?perf=1` readout shows: how many pictures play on screen now, on how many tokens, and how many capped steps have run. */
export const animationStats = { playing: 0, steps: 0, tokens: 0 };
